export interface CoverageZone {
  slug: string;
  title: string;
  intro: string;
  description: string;
  cities: string[];
  color: string;
  category: 'lower-mainland' | 'fraser-valley' | 'island' | 'interior' | 'northern';
}

export const coverageZones: CoverageZone[] = [
  // ── LOWER MAINLAND ──
  {
    slug: 'metro-vancouver',
    title: 'Metro Vancouver',
    intro: 'Core coverage — same-day, rush, and scheduled delivery.',
    description: 'Our primary service area with the fastest delivery options. Same-day delivery within 3-4 hours, direct delivery within 1-3 hours, and standard next-business-day across all Metro Vancouver cities.',
    cities: [
      'Vancouver', 'Downtown Vancouver', 'East Vancouver', 'West Side Vancouver',
      'Burnaby', 'North Burnaby', 'South Burnaby', 'Metrotown',
      'New Westminster',
      'Richmond', 'Steveston',
      'North Vancouver', 'Lower Lonsdale', 'Lynn Valley',
      'West Vancouver',
      'UBC / University Endowment Lands',
    ],
    color: '#395ACB',
    category: 'lower-mainland',
  },
  {
    slug: 'tri-cities',
    title: 'Tri-Cities',
    intro: 'Full service across Coquitlam, Port Coquitlam, and Port Moody.',
    description: 'Complete courier service covering the Tri-Cities area. Same-day and standard delivery with pickup and drop-off at all business and residential addresses.',
    cities: [
      'Coquitlam', 'Port Coquitlam', 'Port Moody',
      'Burke Mountain', 'Westwood Plateau', 'Eagle Ridge',
      'Anmore', 'Belcarra',
    ],
    color: '#2d4ab5',
    category: 'lower-mainland',
  },
  {
    slug: 'surrey-delta',
    title: 'Surrey & Delta',
    intro: 'Full coverage across Surrey, Delta, White Rock, and Tsawwassen.',
    description: 'Reliable delivery service spanning all of Surrey and Delta including industrial, commercial, and residential areas. Same-day and scheduled routes available.',
    cities: [
      'Surrey', 'North Surrey', 'South Surrey', 'Cloverdale', 'Fleetwood',
      'Guildford', 'Newton', 'Whalley', 'Sullivan',
      'White Rock',
      'Delta', 'North Delta', 'Ladner', 'Tsawwassen',
      'Boundary Bay',
    ],
    color: '#1e40af',
    category: 'lower-mainland',
  },

  // ── FRASER VALLEY ──
  {
    slug: 'langley-aldergrove',
    title: 'Langley & Aldergrove',
    intro: 'Standard and same-day service across Langley Township and City.',
    description: 'Courier service covering all of Langley including the Township, City, Walnut Grove, Willoughby, and Aldergrove areas.',
    cities: [
      'Langley City', 'Langley Township', 'Walnut Grove', 'Willoughby',
      'Murrayville', 'Fort Langley', 'Aldergrove', 'Brookswood',
      'Gloucester Estates',
    ],
    color: '#1e3a8a',
    category: 'fraser-valley',
  },
  {
    slug: 'maple-ridge-pitt-meadows',
    title: 'Maple Ridge & Pitt Meadows',
    intro: 'Reliable service to Maple Ridge, Pitt Meadows, and surrounding areas.',
    description: 'Standard and same-day delivery covering Maple Ridge, Pitt Meadows, and nearby communities including Whonnock and Webster\'s Corners.',
    cities: [
      'Maple Ridge', 'Pitt Meadows',
      'Whonnock', 'Webster\'s Corners', 'Albion',
      'Silver Valley', 'Kanaka Creek',
    ],
    color: '#1e3a8a',
    category: 'fraser-valley',
  },
  {
    slug: 'abbotsford-mission',
    title: 'Abbotsford & Mission',
    intro: 'Next-day and same-day delivery in the central Fraser Valley.',
    description: 'Full courier coverage across Abbotsford and Mission including all commercial and residential zones, industrial parks, and agricultural areas.',
    cities: [
      'Abbotsford', 'Clearbrook', 'Matsqui', 'Mt Lehman',
      'Mission', 'Hatzic', 'Silverdale', 'Stave Falls',
      'Sumas Prairie',
    ],
    color: '#1e3a8a',
    category: 'fraser-valley',
  },
  {
    slug: 'chilliwack-hope',
    title: 'Chilliwack, Hope & Agassiz',
    intro: 'Eastern Fraser Valley coverage for standard and scheduled delivery.',
    description: 'Courier service reaching the eastern Fraser Valley including Chilliwack, Sardis, Vedder Crossing, Hope, Agassiz, and Harrison Hot Springs.',
    cities: [
      'Chilliwack', 'Sardis', 'Vedder Crossing', 'Promontory', 'Rosedale',
      'Hope', 'Silver Creek',
      'Agassiz', 'Harrison Hot Springs', 'Harrison Mills',
      'Kent', 'Ruby Creek',
    ],
    color: '#1e3a8a',
    category: 'fraser-valley',
  },

  // ── VANCOUVER ISLAND ──
  {
    slug: 'vancouver-island',
    title: 'Vancouver Island',
    intro: 'Last-mile delivery from our Lower Mainland network to the island.',
    description: 'SpeedyShip provides last-mile delivery to Vancouver Island from our Lower Mainland and Fraser Valley hub. We coordinate ferry logistics and scheduling to get your packages delivered across the island. Contact us for routing and custom scheduling.',
    cities: [
      'Victoria', 'Saanich', 'Oak Bay', 'Esquimalt', 'Langford', 'Colwood',
      'Sidney', 'Sooke', 'Metchosin', 'View Royal',
      'Duncan', 'Lake Cowichan', 'Mill Bay', 'Cobble Hill', 'Shawnigan Lake',
      'Nanaimo', 'Ladysmith', 'Chemainus', 'Cedar',
      'Parksville', 'Qualicum Beach', 'Errington',
      'Courtenay', 'Comox', 'Cumberland',
      'Campbell River', 'Quadra Island',
      'Port Alberni', 'Ucluelet', 'Tofino',
      'Port Hardy', 'Port McNeill', 'Alert Bay',
      'Salt Spring Island', 'Gabriola Island', 'Galiano Island',
    ],
    color: '#059669',
    category: 'island',
  },

  // ── BC INTERIOR ──
  {
    slug: 'sea-to-sky',
    title: 'Sea-to-Sky Corridor',
    intro: 'Whistler, Squamish, and Pemberton delivery service.',
    description: 'Courier service along the Sea-to-Sky Highway covering Squamish, Whistler, and Pemberton. Ideal for resort businesses, construction sites, and corporate deliveries.',
    cities: [
      'Squamish', 'Brackendale', 'Garibaldi Highlands',
      'Whistler', 'Whistler Village', 'Function Junction',
      'Pemberton', 'Mount Currie',
      'Lions Bay', 'Britannia Beach', 'Furry Creek',
    ],
    color: '#7c3aed',
    category: 'interior',
  },
  {
    slug: 'okanagan',
    title: 'Okanagan',
    intro: 'Kelowna, Kamloops, Vernon, and Penticton coverage.',
    description: 'Regional delivery across the Okanagan and Thompson regions. Standard and scheduled service for businesses needing reliable inter-city courier support.',
    cities: [
      'Kelowna', 'West Kelowna', 'Lake Country', 'Peachland',
      'Vernon', 'Coldstream', 'Armstrong', 'Enderby',
      'Penticton', 'Summerland', 'Oliver', 'Osoyoos',
      'Kamloops', 'Sun Peaks',
      'Salmon Arm', 'Sicamous',
      'Merritt',
    ],
    color: '#7c3aed',
    category: 'interior',
  },
  {
    slug: 'kootenays',
    title: 'Kootenays & Columbia',
    intro: 'Nelson, Cranbrook, Revelstoke, and surrounding areas.',
    description: 'Courier service for the Kootenay and Columbia regions. Contact us for custom routing and scheduling for these extended service areas.',
    cities: [
      'Nelson', 'Castlegar', 'Trail', 'Rossland',
      'Cranbrook', 'Kimberley', 'Fernie', 'Invermere',
      'Revelstoke', 'Golden',
      'Creston', 'Nakusp', 'New Denver',
    ],
    color: '#7c3aed',
    category: 'interior',
  },

  // ── NORTHERN BC ──
  {
    slug: 'northern-bc',
    title: 'Northern BC',
    intro: 'Prince George, Prince Rupert, Terrace, and the Peace Region.',
    description: 'Extended delivery coverage for Northern British Columbia. Scheduled routes and custom logistics for businesses in the north. Contact dispatch for availability and lead times.',
    cities: [
      'Prince George', 'Mackenzie', 'Valemount', 'McBride',
      'Prince Rupert', 'Terrace', 'Kitimat', 'Smithers', 'Houston', 'Burns Lake',
      'Vanderhoof', 'Fort St. James',
      'Williams Lake', 'Quesnel', '100 Mile House', '150 Mile House',
      'Dawson Creek', 'Fort St. John', 'Chetwynd', 'Tumbler Ridge', 'Hudson\'s Hope',
      'Fort Nelson',
    ],
    color: '#991b1b',
    category: 'northern',
  },
];

export const coverageCategories = [
  { key: 'lower-mainland', title: 'Lower Mainland' },
  { key: 'fraser-valley', title: 'Fraser Valley' },
  { key: 'island', title: 'Vancouver Island' },
  { key: 'interior', title: 'BC Interior' },
  { key: 'northern', title: 'Northern BC' },
] as const;

export function getZoneBySlug(slug: string): CoverageZone | undefined {
  return coverageZones.find(z => z.slug === slug);
}

export function getZonesByCategory(category: string): CoverageZone[] {
  return coverageZones.filter(z => z.category === category);
}
